import { useState, useRef, useEffect } from "react";
import { Shell } from "@/components/layout/Shell";
import { useParams, useLocation } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { authenticatedFetch, queryClient } from "@/lib/queryClient";
import { api } from "@shared/routes";
import { uploadRemontaPhoto } from "@/lib/remonta-photo-upload";
import { canEmitReclassification } from "@/lib/reclassification-emission";
import { A1SigningDialog } from "@/components/laudo/A1SigningDialog";
import {
  emitReclassificationPdf,
  pdf,
} from "@/lib/remonta-laudo-pdf";
import {
  LaudoAvaliacaoPdf,
  type LaudoAvaliacaoWithDetails,
} from "@/lib/laudo-avaliacao-pdf";
import {
  useRemontaLaudo,
  useRemontaItensLaudo,
  useRemontaFotos,
  useRemontaReparos,
} from "@/hooks/use-data";
import {
  LAUDO_STATUS_LABELS,
  LAUDO_STATUS_COLORS,
  VEICULO_TIPO_LABELS,
  CLASSIFICACAO_PORTE_LABELS,
  ELEMENTO_STATUS_LABELS,
  ELEMENTO_STATUS_COLORS,
  CONCLUSAO_LABELS,
  CONCLUSAO_COLORS,
  NEXO_CLASSIFICACAO_LABELS,
  NEXO_CLASSIFICACAO_COLORS,
  TIPO_FOTO_LABELS,
  RECLASSIFICACAO_RESPOSTA_LABELS,
  RECLASSIFICACAO_VISTAS,
  CORES,
} from "@/lib/constants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Download,
  Camera,
  Trash2,
  CheckCircle2,
  Car,
  User,
  Loader2,
  Building2,
  X,
  Eye,
  RotateCcw,
} from "lucide-react";

const STATUS_ORDER = [
  "rascunho",
  "em_vistoria",
  "em_avaliacao_rt",
  "finalizado",
];

const VEICULO_FOTO_ORDER = [
  "veiculo_frente",
  "veiculo_traseira",
  "veiculo_lateral_esq",
  "veiculo_lateral_dir",
  "veiculo_interior",
  "veiculo_km",
  "veiculo_motor",
  "veiculo_identificacao",
];
const VEICULO_FOTO_REQUIRED = [
  "veiculo_frente",
  "veiculo_traseira",
  "veiculo_lateral_esq",
  "veiculo_lateral_dir",
];

export default function LaudoDetalhes() {
  const { id: laudoId } = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const { session, profile } = useAuth();
  const { toast } = useToast();

  const {
    data: laudo,
    isLoading: laudoLoading,
    isError: laudoError,
  } = useRemontaLaudo(laudoId);
  const { data: itens = [], isLoading: itensLoading } =
    useRemontaItensLaudo(laudoId);
  const { data: fotos = [], isLoading: fotosLoading } =
    useRemontaFotos(laudoId);
  const { data: reparos = [], isLoading: reparosLoading } =
    useRemontaReparos(laudoId);
  const { data: dossier } = useQuery<any>({
    queryKey: [api.remontaDossies.get.path, laudo?.dossieId],
    queryFn: async () => {
      const response = await authenticatedFetch(
        api.remontaDossies.get.path.replace(":id", laudo.dossieId),
        { headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {} },
      );
      if (!response.ok) throw new Error("Erro ao carregar Dossie");
      return response.json();
    },
    enabled: laudo?.tipo === "reclassificacao" && !!laudo?.dossieId && !!session?.access_token,
  });

  const roles: string[] = profile?.roles ?? [];
  const isParceiro = roles.includes("parceiro_negocios");
  const [activeTab, setActiveTab] = useState(
    isParceiro ? "fotos" : "elementos",
  );
  const [reparoForm, setReparoForm] = useState({
    descricao: "",
    numeroNf: "",
    valor: "",
    fornecedor: "",
  });
  const [conclusaoForm, setConclusaoForm] = useState<{
    conclusao: "recuperavel" | "irrecuperavel" | "";
    justificativa: string;
    classificacaoMonta: string;
  }>({ conclusao: "", justificativa: "", classificacaoMonta: "" });
  const [nexoForm, setNexoForm] = useState({
    nexoNarrativa: laudo?.nexoNarrativa ?? "",
    nexoDinamica: laudo?.nexoDinamica ?? "",
    nexoGeometria: laudo?.nexoGeometria ?? "",
    nexoIndicativos: laudo?.nexoIndicativos ?? "",
    nexoClassificacao: laudo?.nexoClassificacao ?? "",
  });
  const [showFinalizeDialog, setShowFinalizeDialog] = useState(false);
  const [isAdvancingStatus, setIsAdvancingStatus] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [signingOpen, setSigningOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [compareAId, setCompareAId] = useState<string>("");
  const [compareBId, setCompareBId] = useState<string>("");
  const [compareOpen, setCompareOpen] = useState(false);

  function solveColor(corVeiculo: string): {
    codigo: string;
    nome: string;
    hex: string | null;
  } {
    const codigoCor = corVeiculo.split("-")[0].trim();

    const corFinal = CORES.filter((c) => c.codigo === codigoCor)[0];
    return corFinal;
  }

  async function handleGerarPdf() {
    if (!laudo) return;
    setIsGeneratingPdf(true);
    try {
      const emitted = await emitReclassificationPdf(laudoId, session?.access_token);
      queryClient.invalidateQueries({
        queryKey: [api.remontaLaudos.get.path, laudoId],
      });
      window.open(emitted.pdfUrl, "_blank", "noopener,noreferrer");

      toast({ title: "Laudo assinado e emitido com sucesso." });
    } catch (err: any) {
      throw new Error(err?.message ?? "Erro ao assinar o PDF. Tente novamente.");
    } finally {
      setIsGeneratingPdf(false);
    }
  }
  const [localItems, setLocalItems] = useState<
    Record<string, { status: string; observacao: string }>
  >({});
  const [uploadingElemento, setUploadingElemento] = useState<string | null>(
    null,
  );
  const [selectedElementoFotoId, setSelectedElementoFotoId] =
    useState<string>("");
  const saveTimerRef = useRef<Record<string, ReturnType<typeof setTimeout>>>(
    {},
  );
  const elementoFileInputRef = useRef<HTMLInputElement>(null);
  const uploadingElementoRef = useRef<string | null>(null);

  const isRt = roles.includes("rt");
  const isReclassification = laudo?.tipo === "reclassificacao";
  const canEditReclassification = !!(
    isReclassification && isRt && profile?.id === laudo?.rtId &&
    !["finalizado", "cancelado", "em_auditoria"].includes(laudo?.status)
  );

  // ── Status advance ──────────────────────────────────────────────────────

  async function advanceStatus(nextStatus: string) {
    if (!laudoId) return;
    setIsAdvancingStatus(true);
    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (session?.access_token)
        headers.Authorization = `Bearer ${session.access_token}`;
      const res = await authenticatedFetch(`/api/remonta/laudos/${laudoId}/status`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ status: nextStatus }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Erro" }));
        throw new Error(err.message);
      }
      queryClient.invalidateQueries({
        queryKey: [api.remontaLaudos.get.path, laudoId],
      });
      toast({ title: "Status atualizado." });
    } catch (err: any) {
      toast({ title: err.message, variant: "destructive" });
    } finally {
      setIsAdvancingStatus(false);
    }
  }

  // ── Element item update ─────────────────────────────────────────────────

  function handleStatusChange(item: any, newStatus: string) {
    const observacao = localItems[item.id]?.observacao ?? item.observacao ?? "";
    setLocalItems((prev) => ({
      ...prev,
      [item.id]: {
        ...prev[item.id],
        status: newStatus,
        observacao,
      },
    }));
    if (isReclassification && newStatus === "na" && !observacao.trim()) return;
    sendItemUpdate(item, isReclassification ? { status: newStatus, observacao } : { status: newStatus });
  }

  function handleObservacaoChange(item: any, value: string) {
    setLocalItems((prev) => ({
      ...prev,
      [item.id]: {
        ...prev[item.id],
        observacao: value,
        status: prev[item.id]?.status ?? item.status,
      },
    }));
    clearTimeout(saveTimerRef.current[item.id]);
    const status = localItems[item.id]?.status ?? item.status;
    if (isReclassification && status === "na" && !value.trim()) return;
    saveTimerRef.current[item.id] = setTimeout(() => {
      sendItemUpdate(item, isReclassification ? { status, observacao: value } : { observacao: value });
    }, 800);
  }

  async function sendItemUpdate(item: any, data: Record<string, string>) {
    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (session?.access_token)
        headers.Authorization = `Bearer ${session.access_token}`;
      const res = await authenticatedFetch(
        `/api/remonta/laudos/${laudoId}/itens/${item.elementoId}`,
        { method: "PUT", headers, body: JSON.stringify(data) },
      );
      if (!res.ok) {
        const err = await res
          .json()
          .catch(() => ({ message: "Erro ao salvar" }));
        throw new Error(err.message);
      }
    } catch (err: any) {
      toast({ title: err.message, variant: "destructive" });
      // Revert local state on error
      setLocalItems((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
    }
  }

  // ── Elemento photo upload ───────────────────────────────────────────────────

  async function handleElementoPhotoSelected(file: File, elementoId: string) {
    if (
      !["image/jpeg", "image/png", "image/webp"].includes(file.type) ||
      file.size > 10 * 1024 * 1024
    ) {
      toast({
        title: "Arquivo inválido. Use JPG, PNG ou WEBP até 10 MB.",
        variant: "destructive",
      });
      return;
    }
    setUploadingElemento(elementoId);
    try {
      await uploadRemontaPhoto({
        laudoId,
        file,
        elementoId,
        tipoFoto: "elemento",
        accessToken: session?.access_token,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/remonta/laudos", laudoId, "fotos"],
      });
    } catch {
      toast({
        title: "Erro ao registrar foto. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setUploadingElemento(null);
    }
  }

  // ── Veiculo photo upload (Avaliação) ────────────────────────────────────

  async function handleVeiculoPhotoSelected(file: File, tipoFoto: string) {
    const allowedTypes = isReclassification
      ? ["image/jpeg", "image/png"]
      : ["image/jpeg", "image/png", "image/webp"];
    if (
      !allowedTypes.includes(file.type) ||
      file.size > 10 * 1024 * 1024
    ) {
      toast({
        title: `Arquivo inválido. Use ${isReclassification ? "JPG ou PNG" : "JPG, PNG ou WEBP"} até 10 MB.`,
        variant: "destructive",
      });
      return;
    }
    setUploadingElemento(tipoFoto);
    try {
      await uploadRemontaPhoto({
        laudoId,
        file,
        tipoFoto,
        accessToken: session?.access_token,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/remonta/laudos", laudoId, "fotos"],
      });
    } catch {
      toast({
        title: "Erro ao registrar foto. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setUploadingElemento(null);
    }
  }

  // ── Valor Desejado (Avaliação) ──────────────────────────────────────────

  const [valorDesejadoInput, setValorDesejadoInput] = useState(
    laudo?.valorDesejado != null ? String(laudo.valorDesejado) : "",
  );

  // laudo chega async (useQuery) — o useState acima só roda no mount, então
  // sincroniza aqui quando o valor do servidor aparece/muda.
  useEffect(() => {
    setValorDesejadoInput(
      laudo?.valorDesejado != null ? String(laudo.valorDesejado) : "",
    );
  }, [laudo?.valorDesejado]);

  // ── Nexo Causal (Reclassificação) ────────────────────────────────────────

  // Guarda o último snapshot vindo do servidor. Usado para só resincronizar
  // o form quando ele ainda estiver "limpo" (ninguém digitou nada desde a
  // última sincronização) — ver comentário no useEffect abaixo.
  const nexoSyncedRef = useRef<typeof nexoForm | null>(null);

  // laudo chega async (useQuery), e a mesma query key é invalidada por ~6
  // outras mutations desta página (foto, checklist, status, valorDesejado)
  // além de refetchOnWindowFocus. Se resincronizássemos incondicionalmente
  // (como o valorDesejadoInput faz), um refetch em background enquanto o RT
  // digita — ou mesmo o próprio nexoCausalMutation invalidando a query no
  // meio de uma digitação — apagaria texto ainda não salvo. Por isso só
  // aplicamos o valor do servidor quando o form atual ainda é igual ao
  // último snapshot sincronizado (ou seja, "pristine"); se o usuário editou
  // algo desde então, mantemos o que está na tela. NÃO simplificar para uma
  // sincronização incondicional.
  useEffect(() => {
    const serverValues = {
      nexoNarrativa: laudo?.nexoNarrativa ?? "",
      nexoDinamica: laudo?.nexoDinamica ?? "",
      nexoGeometria: laudo?.nexoGeometria ?? "",
      nexoIndicativos: laudo?.nexoIndicativos ?? "",
      nexoClassificacao: laudo?.nexoClassificacao ?? "",
    };
    setNexoForm((current) => {
      const synced = nexoSyncedRef.current;
      const isPristine =
        synced === null ||
        (Object.keys(serverValues) as (keyof typeof serverValues)[]).every(
          (key) => current[key] === synced[key],
        );
      nexoSyncedRef.current = serverValues;
      return isPristine ? serverValues : current;
    });
  }, [
    laudo?.nexoNarrativa,
    laudo?.nexoDinamica,
    laudo?.nexoGeometria,
    laudo?.nexoIndicativos,
    laudo?.nexoClassificacao,
  ]);

  const nexoCausalMutation = useMutation({
    mutationFn: async () => {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (session?.access_token)
        headers.Authorization = `Bearer ${session.access_token}`;
      const res = await authenticatedFetch(`/api/remonta/laudos/${laudoId}/nexo-causal`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({
          nexoNarrativa: nexoForm.nexoNarrativa,
          nexoDinamica: nexoForm.nexoDinamica,
          nexoGeometria: nexoForm.nexoGeometria,
          nexoIndicativos: nexoForm.nexoIndicativos,
          nexoClassificacao: nexoForm.nexoClassificacao || undefined,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Erro" }));
        throw new Error(err.message);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [api.remontaLaudos.get.path, laudoId],
      });
      toast({ title: "Nexo causal salvo com sucesso." });
    },
    onError: (err: Error) =>
      toast({ title: err.message, variant: "destructive" }),
  });

  const valorDesejadoMutation = useMutation({
    mutationFn: async (valorDesejado: string) => {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (session?.access_token)
        headers.Authorization = `Bearer ${session.access_token}`;
      const res = await authenticatedFetch(`/api/remonta/laudos/${laudoId}/valor-desejado`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({
          valorDesejado: valorDesejado === "" ? null : valorDesejado,
        }),
      });
      if (!res.ok) {
        const err = await res
          .json()
          .catch(() => ({ message: "Erro ao salvar valor desejado." }));
        throw new Error(err.message);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [api.remontaLaudos.get.path, laudoId],
      });
    },
    onError: (err: Error) =>
      toast({ title: err.message, variant: "destructive" }),
  });

  // ── Gerar PDF (Avaliação) ────────────────────────────────────────────────

  async function handleGerarPdfAvaliacao() {
    if (!laudo) return;
    setIsGeneratingPdf(true);
    try {
      const pdfData: LaudoAvaliacaoWithDetails = {
        ...laudo,
        fotos: (fotos ?? []).map((f: any) => ({
          id: f.id,
          storageUrl: f.storageUrl,
          tipoFoto: f.tipoFoto,
        })),
        sinistroLocalizacao: laudo.sinistroLocalizacao
          ? {
              endereco: laudo.sinistroLocalizacao.endereco,
              desmontes: (laudo.sinistroLocalizacao.desmontes ?? []).map(
                (d: any) => ({
                  nome: d.nome,
                  telefone: d.telefone,
                  endereco: d.endereco,
                }),
              ),
            }
          : null,
      };
      const blob = await pdf(<LaudoAvaliacaoPdf laudo={pdfData} />).toBlob();

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (session?.access_token)
        headers.Authorization = `Bearer ${session.access_token}`;

      // Cada geração vai para um caminho novo (sem sobrescrever) pelo link de envio validado da API.
      const intentRes = await authenticatedFetch(`/api/remonta/laudos/${laudoId}/pdf-upload-intent`, {
        method: "POST",
        headers,
      });
      if (!intentRes.ok) throw new Error("Erro ao preparar envio do PDF.");
      const { storagePath, signedUrl } = (await intentRes.json()) as {
        storagePath: string;
        signedUrl: string;
      };
      const uploadRes = await fetch(signedUrl, {
        method: "PUT",
        headers: { "Content-Type": "application/pdf" },
        body: blob,
      });
      if (!uploadRes.ok) throw new Error("Erro ao salvar PDF no storage.");

      const updateRes = await authenticatedFetch(`/api/remonta/laudos/${laudoId}/pdf-url`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ storagePath }),
      });
      if (!updateRes.ok) {
        throw new Error("Erro ao salvar URL do PDF.");
      }

      queryClient.invalidateQueries({
        queryKey: [api.remontaLaudos.get.path, laudoId],
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const placa = laudo.veiculo?.placa ?? "sem-placa";
      const date = new Date().toISOString().slice(0, 10);
      a.download = `laudo-avaliacao-${placa}-${date}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({ title: "PDF gerado e salvo com sucesso!" });
    } catch (err: any) {
      toast({
        title: err?.message ?? "Erro ao gerar PDF. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingPdf(false);
    }
  }

  async function handleFinalizarEGerarPdfAvaliacao() {
    try {
      if (laudo?.status !== "finalizado") {
        await finalizeMutation.mutateAsync();
      }
      await handleGerarPdfAvaliacao();
    } catch {
      // finalizeMutation's onError already toasts; nothing further to do here.
    }
  }

  // ── Delete foto ─────────────────────────────────────────────────────────

  async function deleteFoto(fotoId: string) {
    try {
      const headers: Record<string, string> = {};
      if (session?.access_token)
        headers.Authorization = `Bearer ${session.access_token}`;
      const res = await authenticatedFetch(`/api/remonta/fotos/${fotoId}`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok) {
        const err = await res
          .json()
          .catch(() => ({ message: "Erro ao remover foto." }));
        throw new Error(err.message);
      }
      queryClient.invalidateQueries({
        queryKey: ["/api/remonta/laudos", laudoId, "fotos"],
      });
      toast({ title: "Foto removida." });
    } catch (err: any) {
      toast({
        title: err.message ?? "Erro ao remover foto.",
        variant: "destructive",
      });
    }
  }

  // ── Reparo create ───────────────────────────────────────────────────────

  const createReparoMutation = useMutation({
    mutationFn: async () => {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (session?.access_token)
        headers.Authorization = `Bearer ${session.access_token}`;
      const res = await authenticatedFetch(`/api/remonta/laudos/${laudoId}/reparos`, {
        method: "POST",
        headers,
        body: JSON.stringify(reparoForm),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Erro" }));
        throw new Error(err.message);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/remonta/laudos", laudoId, "reparos"],
      });
      setReparoForm({ descricao: "", numeroNf: "", valor: "", fornecedor: "" });
      toast({ title: "Reparo registrado com sucesso." });
    },
    onError: (err: Error) =>
      toast({ title: err.message, variant: "destructive" }),
  });

  async function deleteReparo(reparoId: string) {
    try {
      const headers: Record<string, string> = {};
      if (session?.access_token)
        headers.Authorization = `Bearer ${session.access_token}`;
      await authenticatedFetch(`/api/remonta/reparos/${reparoId}`, {
        method: "DELETE",
        headers,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/remonta/laudos", laudoId, "reparos"],
      });
      toast({ title: "Reparo removido." });
    } catch {
      toast({ title: "Erro ao remover reparo.", variant: "destructive" });
    }
  }

  // ── Finalize ────────────────────────────────────────────────────────────

  const finalizeMutation = useMutation({
    mutationFn: async () => {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (session?.access_token)
        headers.Authorization = `Bearer ${session.access_token}`;
      const res = await authenticatedFetch(`/api/remonta/laudos/${laudoId}/finalize`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(isReclassification ? {} : {
          conclusao: conclusaoForm.conclusao,
          justificativa: conclusaoForm.justificativa,
          classificacaoMonta: conclusaoForm.classificacaoMonta,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Erro" }));
        throw new Error(err.message);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [api.remontaLaudos.get.path, laudoId],
      });
      if (laudo?.dossieId) queryClient.invalidateQueries({ queryKey: [api.remontaDossies.get.path, laudo.dossieId] });
      toast({ title: isReclassification ? "Inspeção validada." : "Laudo finalizado com sucesso." });
      setShowFinalizeDialog(false);
    },
    onError: (err: Error) =>
      toast({ title: err.message, variant: "destructive" }),
  });

  const createRevisionMutation = useMutation({
    mutationFn: async () => {
      const res = await authenticatedFetch(api.remontaReclassificacao.createRevision.path.replace(":id", laudoId), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ rtId: laudo?.rtId }),
      });
      const body = await res.json().catch(() => ({ message: "Erro ao criar revisão." }));
      if (!res.ok) throw new Error(body.message);
      return body;
    },
    onSuccess: (revision) => navigate(`/laudos/${revision.id}`),
    onError: (error: Error) => toast({ title: error.message, variant: "destructive" }),
  });

  // ── Auditoria (decisão aprovar/devolver) ────────────────────────────────

  const [decisaoJustificativa, setDecisaoJustificativa] = useState("");

  const auditoriaMutation = useMutation({
    mutationFn: async (decisao: "aprovado" | "devolvido") => {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (session?.access_token)
        headers.Authorization = `Bearer ${session.access_token}`;
      const res = await authenticatedFetch(`/api/remonta/laudos/${laudoId}/auditoria`, {
        method: "POST",
        headers,
        body: JSON.stringify({ decisao, justificativa: decisaoJustificativa }),
      });
      if (!res.ok) {
        const err = await res
          .json()
          .catch(() => ({ message: "Erro ao decidir auditoria" }));
        throw new Error(err.message);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [api.remontaLaudos.get.path, laudoId],
      });
      setDecisaoJustificativa("");
      toast({ title: "Decisão registrada." });
    },
    onError: (err: Error) =>
      toast({ title: err.message, variant: "destructive" }),
  });

  // ── Render ──────────────────────────────────────────────────────────────

  if (laudoLoading) {
    return (
      <Shell>
        <div className="space-y-4">
          <Skeleton className="h-32 w-full rounded-lg" />
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-64 w-full rounded-lg" />
        </div>
      </Shell>
    );
  }

  if (!laudo) {
    return (
      <Shell>
        <div className="text-center py-16 text-slate-500">
          <p>
            {laudoError
              ? "Sem permissão para acessar este laudo."
              : "Laudo não encontrado."}
          </p>
          <Button
            variant="ghost"
            className="mt-4"
            onClick={() => navigate("/laudos")}
          >
            ← Voltar para Laudos
          </Button>
        </div>
      </Shell>
    );
  }

  if (laudo?.tipo === "avaliacao") {
    const presentTipos = new Set(fotos.map((f: any) => f.tipoFoto));
    const missingCount = VEICULO_FOTO_REQUIRED.filter(
      (t) => !presentTipos.has(t),
    ).length;
    const canFinalize = missingCount === 0 && !!laudo.valorDesejado;

    return (
      <Shell>
        <div className="space-y-5">
          {/* Heading row */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <Button
                variant="ghost"
                size="sm"
                className="mb-1 -ml-2 text-slate-500"
                onClick={() => navigate("/laudos")}
              >
                ← Laudos
              </Button>
              <h1 className="text-2xl font-semibold">
                {laudo.codigo ?? `Laudo #${laudo.id.slice(0, 8)}`}
              </h1>
              <p className="text-sm text-slate-500">
                {laudo.veiculo?.placa ?? "Veículo não identificado"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge
                className={
                  LAUDO_STATUS_COLORS[laudo.status] ??
                  "bg-slate-100 text-slate-700"
                }
              >
                {LAUDO_STATUS_LABELS[laudo.status] ?? laudo.status}
              </Badge>
              {laudo.pdfUrl && (
                <Button variant="outline" size="sm" asChild>
                  <a
                    href={laudo.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Ver PDF
                  </a>
                </Button>
              )}
            </div>
          </div>

          {/* Vehicle header card */}
          <Card className="border border-slate-200">
            <CardContent className="pt-4 pb-4 space-y-3">
              {laudo.veiculo && (
                <>
                  <div className="flex items-center gap-2 text-sm text-slate-600 flex-wrap">
                    <Car className="h-4 w-4 text-slate-400" />
                    <span className="font-semibold">{laudo.veiculo.placa}</span>
                    <span className="text-slate-400">·</span>
                    <span>
                      {[
                        laudo.veiculo.marca,
                        laudo.veiculo.modelo,
                        laudo.veiculo.anoFabricacao,
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    </span>
                    {laudo.veiculo.tipoVeiculo && (
                      <Badge className="text-xs bg-blue-100 text-blue-700">
                        {VEICULO_TIPO_LABELS[laudo.veiculo.tipoVeiculo] ??
                          laudo.veiculo.tipoVeiculo}
                      </Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm text-slate-600 pl-6">
                    {laudo.veiculo.chassi && (
                      <span>Chassi: {laudo.veiculo.chassi}</span>
                    )}
                    {laudo.veiculo.renavam && (
                      <span>RENAVAM: {laudo.veiculo.renavam}</span>
                    )}
                    {laudo.veiculo.cor && <span>Cor: {laudo.veiculo.cor}</span>}
                    {laudo.veiculo.classificacaoPorte && (
                      <span>
                        Classificação:{" "}
                        {CLASSIFICACAO_PORTE_LABELS[
                          laudo.veiculo.classificacaoPorte
                        ] ?? laudo.veiculo.classificacaoPorte}
                      </span>
                    )}
                    {laudo.veiculo.valorFipe != null && (
                      <span>
                        Valor FIPE:{" "}
                        {Number(laudo.veiculo.valorFipe).toLocaleString(
                          "pt-BR",
                          {
                            style: "currency",
                            currency: "BRL",
                          },
                        )}
                      </span>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Registro Fotográfico */}
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle className="text-base font-semibold">
                Registro Fotográfico do Bem Avaliado
              </CardTitle>
              <p className="text-sm text-slate-500">
                Fotos obrigatórias: Frente, Traseira, Lateral esquerda, Lateral
                direita. As demais são opcionais.
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {VEICULO_FOTO_ORDER.map((tipo) => {
                  const foto = fotos.find((f: any) => f.tipoFoto === tipo);
                  const isRequired = VEICULO_FOTO_REQUIRED.includes(tipo);
                  const isUploading = uploadingElemento === tipo;
                  return (
                    <div key={tipo} className="space-y-1">
                      <p className="text-xs font-semibold flex items-center gap-1">
                        {TIPO_FOTO_LABELS[tipo] ?? tipo}
                        {isRequired && <span className="text-red-500">*</span>}
                      </p>
                      {foto ? (
                        <div className="relative group">
                          <img
                            src={foto.storageUrl}
                            alt={TIPO_FOTO_LABELS[tipo] ?? tipo}
                            className="w-full h-24 object-cover rounded-md border border-slate-200 cursor-pointer"
                            onClick={() => setPreviewImage(foto.storageUrl)}
                          />
                          <button
                            type="button"
                            onClick={() => deleteFoto(foto.id)}
                            className="absolute top-1 right-1 bg-white/90 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            aria-label="Remover foto"
                          >
                            <Trash2 className="h-3.5 w-3.5 text-red-500" />
                          </button>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center h-24 rounded-md border border-dashed border-slate-300 cursor-pointer hover:bg-slate-50">
                          {isUploading ? (
                            <Loader2 className="h-5 w-5 text-slate-400 animate-spin" />
                          ) : (
                            <Camera className="h-5 w-5 text-slate-400" />
                          )}
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            className="hidden"
                            disabled={isUploading}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleVeiculoPhotoSelected(file, tipo);
                              e.target.value = "";
                            }}
                          />
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Valor Desejado */}
          <Card className="border border-slate-200">
            <CardContent className="pt-4 pb-4">
              <Label htmlFor="valor-desejado" className="text-sm font-semibold">
                Valor Desejado
              </Label>
              <CurrencyInput
                id="valor-desejado"
                placeholder="0,00"
                value={valorDesejadoInput}
                onChange={setValorDesejadoInput}
                className="mt-1 h-8 text-sm max-w-xs"
                onBlur={() => valorDesejadoMutation.mutate(valorDesejadoInput)}
              />
            </CardContent>
          </Card>

          {/* Finalize */}
          <div className="flex flex-col items-end gap-1">
            <Button
              onClick={handleFinalizarEGerarPdfAvaliacao}
              disabled={
                !canFinalize || finalizeMutation.isPending || isGeneratingPdf
              }
            >
              {(finalizeMutation.isPending || isGeneratingPdf) && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {laudo.pdfUrl ? "Gerar PDF novamente" : "Finalizar e Gerar PDF"}
            </Button>
            {missingCount > 0 && (
              <p className="text-sm text-red-500">
                Faltam {missingCount} foto(s) obrigatória(s) para finalizar.
              </p>
            )}
          </div>
        </div>

        <Dialog
          open={!!previewImage}
          onOpenChange={(open) => !open && setPreviewImage(null)}
        >
          <DialogContent className="max-w-3xl p-2">
            {previewImage && (
              <img
                src={previewImage}
                alt="Foto ampliada"
                className="w-full h-auto max-h-[85vh] object-contain rounded-md"
              />
            )}
          </DialogContent>
        </Dialog>
      </Shell>
    );
  }

  const currentStepIdx = STATUS_ORDER.indexOf(laudo.status);

  // Group itens by categoriaId
  const categoriaMap = new Map<string, any[]>();
  for (const item of itens) {
    const catId =
      item.elemento?.categoriaId ?? item.categoriaId ?? "sem-categoria";
    if (!categoriaMap.has(catId)) categoriaMap.set(catId, []);
    categoriaMap.get(catId)!.push(item);
  }

  const evaluatedCount = itens.filter(
    (item: any) => {
      const status = localItems[item.id]?.status ?? item.status;
      if (!isReclassification) return status !== "nao_avaliado";
      const observacao = localItems[item.id]?.observacao ?? item.observacao ?? "";
      return ["sim", "nao", "na"].includes(status) && (status !== "na" || observacao.trim());
    },
  ).length;
  const progressPct =
    itens.length > 0 ? Math.round((evaluatedCount / itens.length) * 100) : 0;
  const currentPhotoTypes = new Set(fotos.map((foto: any) => foto.tipoFoto));
  const legalItemsComplete = itens.length > 0 && itens.every((item: any) => {
    const status = localItems[item.id]?.status ?? item.status;
    const observacao = localItems[item.id]?.observacao ?? item.observacao ?? "";
    return ["sim", "nao", "na"].includes(status) && (status !== "na" || observacao.trim());
  });

  const legalPhotosComplete = RECLASSIFICACAO_VISTAS.every((tipo) => currentPhotoTypes.has(tipo)) &&
    (!itens.some((item: any) => (localItems[item.id]?.status ?? item.status) === "sim") || currentPhotoTypes.has("dano"));
  const inspectionComplete = legalItemsComplete && legalPhotosComplete;
  const canEmit = canEmitReclassification({ report: laudo, dossier, profile });

  function fotoLabel(foto: any): string {
    if (foto.elementoId) {
      const item = itens.find((i: any) => i.elementoId === foto.elementoId);
      const nome =
        item?.elemento?.nome ??
        item?.elementoNome ??
        foto.elementoId?.slice(0, 8);
      return `Elemento — ${nome}`;
    }
    return TIPO_FOTO_LABELS[foto.tipoFoto] ?? foto.tipoFoto ?? "Foto";
  }

  return (
    <Shell>
      <div className="space-y-5">
        {/* Heading row */}
        <div className="mb-6 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
          <div>
            <Button
              variant="ghost"
              size="sm"
              className="mb-1 -ml-2 text-slate-500"
              onClick={() => navigate("/laudos")}
            >
              ← Laudos
            </Button>
            <h1 className="text-2xl font-bold">
              {laudo.codigo ?? `Laudo #${laudo.id.slice(0, 8)}`}
            </h1>
            <p className="text-sm text-slate-500">
              {laudo.veiculo?.placa ?? "Veículo não identificado"}
            </p>
          </div>
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
            <Badge
              className={
                LAUDO_STATUS_COLORS[laudo.status] ??
                "bg-slate-100 text-slate-700"
              }
            >
              {LAUDO_STATUS_LABELS[laudo.status] ?? laudo.status}
            </Badge>
            {(() => {
              const hasPdf = !!(laudo as any).pdfUrl;
              return (
                <>
                  {hasPdf && (
                    <Button variant="outline" size="sm" asChild>
                      <a
                        href={(laudo as any).pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        Ver PDF
                      </a>
                    </Button>
                  )}
                  {isReclassification && canEditReclassification && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-auto min-h-9 max-w-full whitespace-normal text-left sm:whitespace-nowrap"
                      onClick={() => setSigningOpen(true)}
                      disabled={!canEmit || isGeneratingPdf || laudoLoading}
                    >
                      {isGeneratingPdf ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Download className="h-4 w-4 mr-2" />
                      )}
                      Emitir Laudo de Reclassificação
                    </Button>
                  )}
                  {isReclassification && laudo.status === "finalizado" && profile?.empresa?.tipo === "VISTORIADORA" && laudo.rtId && (
                    <Button variant="outline" size="sm" onClick={() => createRevisionMutation.mutate()} disabled={createRevisionMutation.isPending}>
                      {createRevisionMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
                      Criar revisão
                    </Button>
                  )}
                </>
              );
            })()}

          </div>
        </div>

        {/* Painel de decisão de auditoria */}
        {laudo.status === "em_auditoria" && profile?.roles?.includes("auditor") && (
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="pt-4 space-y-3">
              <p className="text-sm font-medium text-blue-900">Decisão de auditoria</p>
              <Textarea
                placeholder="Justificativa (obrigatória, mínimo 10 caracteres)"
                value={decisaoJustificativa}
                onChange={(e) => setDecisaoJustificativa(e.target.value)}
                className="bg-white"
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => auditoriaMutation.mutate("aprovado")}
                  disabled={decisaoJustificativa.trim().length < 10 || auditoriaMutation.isPending}
                >
                  {auditoriaMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Aprovar
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => auditoriaMutation.mutate("devolvido")}
                  disabled={decisaoJustificativa.trim().length < 10 || auditoriaMutation.isPending}
                >
                  Devolver ao RT
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Detail card */}
        <Card className="border border-slate-200">
          <CardContent className="pt-4 pb-4 space-y-3">
            {/* Vehicle */}
            {laudo.veiculo && (
              <>
                <div className="flex items-center gap-2 text-sm text-slate-600 flex-wrap">
                  <Car className="h-4 w-4 text-slate-400" />
                  <span className="font-semibold">{laudo.veiculo.placa}</span>
                  <span className="text-slate-400">·</span>
                  <span>
                    {[
                      laudo.veiculo.marca,
                      laudo.veiculo.modelo,
                      laudo.veiculo.anoFabricacao,
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  </span>
                  {laudo.veiculo.tipoVeiculo && (
                    <Badge className="text-xs bg-blue-100 text-blue-700">
                      {VEICULO_TIPO_LABELS[laudo.veiculo.tipoVeiculo] ??
                        laudo.veiculo.tipoVeiculo}
                    </Badge>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm text-slate-600 pl-6">
                  {laudo.veiculo.chassi && (
                    <span>Chassi: {laudo.veiculo.chassi}</span>
                  )}
                  {laudo.veiculo.renavam && (
                    <span>RENAVAM: {laudo.veiculo.renavam}</span>
                  )}
                  {laudo.veiculo.cor && <span>Cor: {laudo.veiculo.cor}</span>}
                  {laudo.veiculo.classificacaoPorte && (
                    <span>
                      Classificação:{" "}
                      {CLASSIFICACAO_PORTE_LABELS[
                        laudo.veiculo.classificacaoPorte
                      ] ?? laudo.veiculo.classificacaoPorte}
                    </span>
                  )}
                  {laudo.veiculo.valorFipe != null && (
                    <span>
                      Valor FIPE:{" "}
                      {Number(laudo.veiculo.valorFipe).toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      })}
                    </span>
                  )}
                </div>
              </>
            )}

            {/* RT */}
            {laudo.rt && (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <User className="h-4 w-4 text-slate-400" />
                <span>{laudo.rt.nome ?? "RT não identificado"}</span>
                {laudo.rt.crea && (
                  <>
                    <span className="text-slate-400">·</span>
                    <span>CREA {laudo.rt.crea}</span>
                  </>
                )}
              </div>
            )}

            {isReclassification && (
              <div className="grid grid-cols-2 gap-2 border-t pt-3 text-sm text-slate-600 sm:grid-cols-4">
                <span><strong>Revisão:</strong> {laudo.revisao}</span>
                <span><strong>Anexo:</strong> {laudo.anexoAplicavel}</span>
                <span><strong>ART:</strong> {laudo.art?.numeroArt ?? "Não informada"}</span>
                <span><strong>Emissão:</strong> {laudo.emitidoEm ? new Date(laudo.emitidoEm).toLocaleString("pt-BR") : "Pendente"}</span>
              </div>
            )}

            {/* Parceiro */}
            {laudo.parceiro && (
              <div className="flex items-center gap-2 text-sm">
                <Building2 className="h-4 w-4 text-slate-400 shrink-0" />
                <span className="text-slate-600">Parceiro:</span>
                <span className="font-semibold">
                  {laudo.parceiro.nomeFantasia ?? laudo.parceiro.razaoSocial}
                </span>
              </div>
            )}

            {/* Status stepper */}
            <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:gap-1" role="list">
              {STATUS_ORDER.map((step, idx) => (
                <div
                  key={step}
                  className="min-w-0 flex items-center gap-1"
                  role="listitem"
                >
                  <div
                    className={`w-3 h-3 rounded-full ${idx <= currentStepIdx ? "bg-primary" : "bg-slate-200"}`}
                  />
                  <span className="text-xs text-slate-500">
                    {LAUDO_STATUS_LABELS[step]}
                  </span>
                  {idx < STATUS_ORDER.length - 1 && (
                    <div
                      className={`mx-1 hidden h-0.5 w-8 flex-1 sm:block ${idx < currentStepIdx ? "bg-primary" : "bg-slate-200"}`}
                    />
                  )}
                </div>
              ))}
            </div>

            {/* Action row */}
            <div className="flex items-center gap-2 justify-end flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/laudos")}
              >
                ← Laudos
              </Button>

              {laudo.status === "rascunho" && !isReclassification && (
                <Button
                  size="sm"
                  onClick={() => advanceStatus("em_vistoria")}
                  disabled={isAdvancingStatus || laudo.tipo !== "avaliacao"}
                >
                  {isAdvancingStatus && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  Iniciar Vistoria
                </Button>
              )}

              {laudo.status === "em_vistoria" && (
                <Button
                  size="sm"
                  onClick={() => advanceStatus("em_avaliacao_rt")}
                  disabled={
                    isAdvancingStatus ||
                    fotos.length === 0 ||
                    (isReclassification && !canEditReclassification)
                  }
                  title={
                    fotos.length === 0
                      ? "Adicione ao menos uma foto antes de enviar para avaliação."
                      : undefined
                  }
                >
                  {isAdvancingStatus && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  Enviar para Avaliação RT
                </Button>
              )}
              {isReclassification && canEditReclassification && dossier?.status === "em_inspecao" && (
                <Button size="sm" onClick={() => finalizeMutation.mutate()} disabled={!inspectionComplete || finalizeMutation.isPending}>
                  {finalizeMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Validar inspeção
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList
            className={`grid w-full ${
              isReclassification
                ? isParceiro
                  ? laudo.ordemServicoId ? "grid-cols-2" : "grid-cols-3"
                  : laudo.ordemServicoId ? "grid-cols-3" : "grid-cols-4"
                : isParceiro
                  ? "grid-cols-2"
                  : "grid-cols-4"
            }`}
          >
            {!isParceiro && (
              <TabsTrigger value="elementos">{isReclassification ? "Checklist" : "Elementos Estruturais"}</TabsTrigger>
            )}
            <TabsTrigger value="fotos">Fotos</TabsTrigger>
            {!isParceiro && !isReclassification && <TabsTrigger value="reparos">Reparos</TabsTrigger>}
            <TabsTrigger value="conclusao">Conclusão</TabsTrigger>
            {isReclassification && !laudo.ordemServicoId && (
              <TabsTrigger value="nexo-causal">Nexo Causal histórico</TabsTrigger>
            )}
          </TabsList>

          {/* ── Tab: Elementos (somente RT/Admin) ── */}
          {!isParceiro && (
            <TabsContent value="elementos" className="space-y-4 mt-4">
              {itensLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : (
                <>
                  {isReclassification && (
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <Badge variant="outline">Anexo {(["", "I", "II", "III", "IV"])[laudo.anexoAplicavel] ?? laudo.anexoAplicavel}</Badge>
                      {!canEditReclassification && <p className="text-sm font-medium text-amber-700">{laudo.status === "em_auditoria" ? "Laudo em auditoria: somente leitura até a decisão." : laudo.status === "finalizado" ? "Laudo emitido: checklist somente leitura." : "Somente o RT atribuído pode registrar esta inspeção."}</p>}
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <Progress
                      value={progressPct}
                      className="h-2 flex-1"
                      aria-label="Progresso de avaliação dos elementos"
                      aria-valuenow={progressPct}
                    />
                    <span className="text-xs text-slate-500 shrink-0">
                      {evaluatedCount} de {itens.length} {isReclassification ? "itens respondidos" : "elementos avaliados"}
                    </span>
                  </div>

                  {itens.length === 0 ? (
                    <p className="text-sm text-slate-500 italic">
                      Nenhum elemento encontrado para este laudo.
                    </p>
                  ) : (
                    <>
                      <input
                        ref={elementoFileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          const elementoId = uploadingElementoRef.current;
                          if (file && elementoId)
                            handleElementoPhotoSelected(file, elementoId);
                          e.target.value = "";
                        }}
                      />
                      <Accordion type="multiple" className="space-y-2">
                        {Array.from(categoriaMap.entries()).map(
                          ([catId, catItens]) => {
                            const catNome =
                              catItens[0]?.elemento?.categoria?.nome ??
                              catItens[0]?.categoriaNome ??
                              `Categoria ${catId.slice(0, 6)}`;
                            return (
                              <AccordionItem
                                key={catId}
                                value={catId}
                                className="border rounded-lg px-4"
                              >
                                <AccordionTrigger className="text-sm font-medium">
                                  {catNome} ({catItens.length})
                                </AccordionTrigger>
                                <AccordionContent className="space-y-4 pb-3">
                                  {catItens.map((item: any) => {
                                    const local = localItems[item.id];
                                    const currentStatus =
                                      local?.status ??
                                      item.status ??
                                      "nao_avaliado";
                                    const currentObs =
                                      local?.observacao ??
                                      item.observacao ??
                                      "";
                                    const elementoNome =
                                      item.elemento?.nome ??
                                      item.elementoNome ??
                                      item.elementoId?.slice(0, 8);
                                    const fotoDoElemento = fotos.find(
                                      (f: any) =>
                                        f.elementoId === item.elementoId,
                                    );
                                    const isUploadingThis =
                                      uploadingElemento === item.elementoId;
                                    return (
                                      <div
                                        key={item.id}
                                        className="space-y-2 pb-3 border-b last:border-0 last:pb-0"
                                      >
                                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                                          <span className="text-sm flex-1 font-medium">
                                            {isReclassification && <span className="mr-2 font-mono text-xs text-slate-500">{item.elemento?.numeroItem}.</span>}
                                            {elementoNome}
                                          </span>
                                          {isReclassification ? (
                                            <RadioGroup
                                              value={["sim", "nao", "na"].includes(currentStatus) ? currentStatus : ""}
                                              onValueChange={(value) => handleStatusChange(item, value)}
                                              disabled={!canEditReclassification}
                                              aria-label={`Item ${item.elemento?.numeroItem} — ${elementoNome}`}
                                              className="grid w-full grid-cols-3 gap-1 sm:w-56"
                                            >
                                              {Object.entries(RECLASSIFICACAO_RESPOSTA_LABELS).map(([value, label]) => (
                                                <Label key={value} htmlFor={`${item.id}-${value}`} className="flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-md border px-2 text-sm has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5">
                                                  <RadioGroupItem id={`${item.id}-${value}`} value={value} />{label}
                                                </Label>
                                              ))}
                                            </RadioGroup>
                                          ) : (
                                            <Select value={currentStatus} onValueChange={(value) => handleStatusChange(item, value)}>
                                              <SelectTrigger className="h-9 w-40" aria-label={`${elementoNome} — status`}><SelectValue /></SelectTrigger>
                                              <SelectContent>{Object.entries(ELEMENTO_STATUS_LABELS).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent>
                                            </Select>
                                          )}
                                        </div>
                                        {(!isReclassification || currentStatus === "na") && <Textarea
                                          className="min-h-[60px] text-sm"
                                          placeholder={isReclassification ? "Justifique por que o item não se aplica" : "Observação (opcional)"}
                                          value={currentObs}
                                          onChange={(e) => handleObservacaoChange(item, e.target.value)}
                                          aria-label={isReclassification ? `Justificativa do item ${item.elemento?.numeroItem}` : `${elementoNome} — observação`}
                                          required={isReclassification}
                                          aria-invalid={isReclassification && !currentObs.trim()}
                                          disabled={isReclassification ? !canEditReclassification : false}
                                        />}
                                        {/* Foto do elemento */}
                                        {(!isReclassification || canEditReclassification) && (fotoDoElemento ? (
                                          <div className="relative w-32 h-24 rounded-md overflow-hidden border border-slate-200 group">
                                            <img
                                              src={fotoDoElemento.storageUrl}
                                              alt={elementoNome}
                                              className="object-cover w-full h-full cursor-pointer"
                                              onClick={() =>
                                                setPreviewImage(
                                                  fotoDoElemento.storageUrl,
                                                )
                                              }
                                            />
                                            <button
                                              type="button"
                                              aria-label="Remover foto"
                                              className="absolute top-1 right-1 bg-black/60 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                                              onClick={() =>
                                                deleteFoto(fotoDoElemento.id)
                                              }
                                            >
                                              <X className="h-3 w-3 text-white" />
                                            </button>
                                          </div>
                                        ) : (
                                          <button
                                            type="button"
                                            disabled={
                                              isUploadingThis ||
                                              (isReclassification && !canEditReclassification)
                                            }
                                            onClick={() => {
                                              uploadingElementoRef.current =
                                                item.elementoId;
                                              elementoFileInputRef.current?.click();
                                            }}
                                            className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 border border-dashed border-slate-300 hover:border-slate-400 rounded-md px-3 py-1.5 transition-colors disabled:opacity-50"
                                          >
                                            {isUploadingThis ? (
                                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                            ) : (
                                              <Camera className="h-3.5 w-3.5" />
                                            )}
                                            {isUploadingThis
                                              ? "Enviando..."
                                              : "Adicionar foto"}
                                          </button>
                                        ))}
                                      </div>
                                    );
                                  })}
                                </AccordionContent>
                              </AccordionItem>
                            );
                          },
                        )}
                      </Accordion>
                    </>
                  )}
                </>
              )}
            </TabsContent>
          )}

          {/* ── Tab: Fotos ── */}
          <TabsContent value="fotos" className="space-y-6 mt-4">
            {/* ── Fotos do veículo (enviadas pelo parceiro) ── */}
            {isReclassification ? (
              <div>
                <h2 className="mb-3 text-base font-semibold">Oito vistas oficiais</h2>
                <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="reclassificacao-vistas">
                  {RECLASSIFICACAO_VISTAS.map((tipo) => {
                    const foto = fotos.find((item: any) => item.tipoFoto === tipo);
                    const uploading = uploadingElemento === tipo;
                    return <div key={tipo} className="min-w-0 space-y-1">
                      <p className="min-h-10 text-xs font-medium leading-5 text-slate-600">{TIPO_FOTO_LABELS[tipo]}</p>
                      {foto ? <div className="group relative aspect-[4/3] overflow-hidden rounded-md border bg-slate-50">
                        <img src={foto.storageUrl} alt={TIPO_FOTO_LABELS[tipo]} className="h-full w-full cursor-pointer object-cover" onClick={() => setPreviewImage(foto.storageUrl)} />
                        {canEditReclassification && <button type="button" aria-label={`Remover ${TIPO_FOTO_LABELS[tipo]}`} onClick={() => deleteFoto(foto.id)} className="absolute right-1 top-1 rounded-full bg-white/90 p-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100"><Trash2 className="h-4 w-4 text-red-600" /></button>}
                      </div> : canEditReclassification ? <label className="flex aspect-[4/3] cursor-pointer flex-col items-center justify-center rounded-md border border-dashed text-xs text-slate-500 hover:bg-slate-50">
                        {uploading ? <Loader2 className="mb-2 h-5 w-5 animate-spin" /> : <Camera className="mb-2 h-5 w-5" />}Capturar
                        <input type="file" accept="image/jpeg,image/png" className="sr-only" disabled={uploading} onChange={(event) => { const file = event.target.files?.[0]; if (file) handleVeiculoPhotoSelected(file, tipo); event.target.value = ""; }} />
                      </label> : <div className="flex aspect-[4/3] items-center justify-center rounded-md border border-dashed text-xs text-slate-400">Pendente</div>}
                    </div>;
                  })}
                </div>
              </div>
            ) : (() => {
              const fotosVeiculo = fotos.filter((f: any) =>
                f.tipoFoto?.startsWith("veiculo_"),
              );
              const TIPO_LABEL: Record<string, string> = {
                veiculo_frente: "Frente",
                veiculo_traseira: "Traseira",
                veiculo_lateral_dir: "Lateral Direita",
                veiculo_lateral_esq: "Lateral Esquerda",
              };
              return (
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                    Fotos do veículo danificado
                  </p>
                  {fotosLoading ? (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {[1, 2, 3, 4].map((i) => (
                        <Skeleton key={i} className="aspect-video rounded-md" />
                      ))}
                    </div>
                  ) : fotosVeiculo.length === 0 ? (
                    <p className="text-sm text-slate-400 italic">
                      Nenhuma foto do veículo enviada pelo parceiro ainda.
                    </p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {fotosVeiculo.map((foto: any) => (
                        <div key={foto.id} className="space-y-1">
                          <div className="rounded-md overflow-hidden border border-slate-200 aspect-video">
                            <img
                              src={foto.storageUrl}
                              alt={TIPO_LABEL[foto.tipoFoto] ?? foto.tipoFoto}
                              className="object-cover w-full h-full cursor-pointer"
                              onClick={() => setPreviewImage(foto.storageUrl)}
                            />
                          </div>
                          <p className="text-xs text-center text-slate-500">
                            {TIPO_LABEL[foto.tipoFoto] ?? foto.tipoFoto}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {isReclassification && <section className="border-t pt-5">
              <h2 className="text-base font-semibold">Fotos das avarias</h2>
              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                {fotos.filter((foto: any) => foto.tipoFoto === "dano").map((foto: any) => <div key={foto.id} className="group relative aspect-[4/3] overflow-hidden rounded-md border bg-slate-50">
                  <img src={foto.storageUrl} alt="Avaria estrutural" className="h-full w-full cursor-pointer object-cover" onClick={() => setPreviewImage(foto.storageUrl)} />
                  {canEditReclassification && <button type="button" aria-label="Remover foto de avaria" onClick={() => deleteFoto(foto.id)} className="absolute right-1 top-1 rounded-full bg-white/90 p-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100"><Trash2 className="h-4 w-4 text-red-600" /></button>}
                </div>)}
                {canEditReclassification && <label className="flex aspect-[4/3] cursor-pointer flex-col items-center justify-center rounded-md border border-dashed text-xs text-slate-500 hover:bg-slate-50">
                  {uploadingElemento === "dano" ? <Loader2 className="mb-2 h-5 w-5 animate-spin" /> : <Camera className="mb-2 h-5 w-5" />}Adicionar avaria
                  <input type="file" accept="image/jpeg,image/png" className="sr-only" disabled={uploadingElemento === "dano"} onChange={(event) => { const file = event.target.files?.[0]; if (file) handleVeiculoPhotoSelected(file, "dano"); event.target.value = ""; }} />
                </label>}
              </div>
            </section>}

            {/* ── Fotos dos elementos (adicionadas pelo RT — parceiro não vê) ── */}
            {!isParceiro && (
              <div className="space-y-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Fotos dos elementos estruturais
                </p>

                {/* Evidências por elemento podem ser registradas pelo RT na WEB e no App. */}
                {isReclassification && <p className="text-sm leading-relaxed text-slate-600">{itens.filter((item: any) => fotos.some((foto: any) => foto.tipoFoto === "elemento" && foto.elementoId === item.elementoId)).length} de {itens.length} elementos com evidência fotográfica. Registre todos, íntegros e danificados, além das oito vistas do veículo.</p>}
                {(!isReclassification || canEditReclassification) && (
                <div className="flex gap-2 items-center">
                  <Select
                    value={selectedElementoFotoId}
                    onValueChange={setSelectedElementoFotoId}
                    disabled={
                      itensLoading ||
                      itens.length === 0 ||
                      (isReclassification && !canEditReclassification)
                    }
                  >
                    <SelectTrigger
                      className="flex-1"
                      aria-label="Selecionar elemento estrutural"
                    >
                      <SelectValue placeholder="Selecione o elemento estrutural..." />
                    </SelectTrigger>
                    <SelectContent>
                      {itens.map((item: any) => {
                        const nome =
                          item.elemento?.nome ??
                          item.elementoNome ??
                          item.elementoId?.slice(0, 8);
                        const temFoto = fotos.some(
                          (f: any) => f.elementoId === item.elementoId,
                        );
                        return (
                          <SelectItem
                            key={item.elementoId}
                            value={item.elementoId}
                          >
                            <span className="flex items-center gap-2">
                              {nome}
                              {temFoto && (
                                <span className="text-xs text-green-600 font-medium">
                                  ● foto
                                </span>
                              )}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={
                      !selectedElementoFotoId ||
                      uploadingElemento === selectedElementoFotoId ||
                      (isReclassification && !canEditReclassification)
                    }
                    onClick={() => {
                      if (selectedElementoFotoId) {
                        uploadingElementoRef.current = selectedElementoFotoId;
                        elementoFileInputRef.current?.click();
                      }
                    }}
                  >
                    {uploadingElemento === selectedElementoFotoId ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                    ) : (
                      <Camera className="h-4 w-4 mr-1.5" />
                    )}
                    {uploadingElemento === selectedElementoFotoId
                      ? "Enviando..."
                      : "Adicionar foto"}
                  </Button>
                </div>
                )}

                {/* Fotos agrupadas por elemento */}
                {fotosLoading ? (
                  <div className="space-y-4">
                    {[1, 2].map((i) => (
                      <div key={i} className="space-y-2">
                        <Skeleton className="h-4 w-40" />
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                          {[1, 2].map((j) => (
                            <Skeleton
                              key={j}
                              className="aspect-square rounded-md"
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  (() => {
                    const fotosElementos = fotos.filter(
                      (f: any) => f.tipoFoto === "elemento" && f.elementoId,
                    );
                    if (fotosElementos.length === 0) {
                      return (
                        <p className="text-sm text-slate-500 italic">
                          Nenhuma foto de elemento adicionada ainda.
                        </p>
                      );
                    }
                    // Agrupar por elementoId
                    const grupos = new Map<
                      string,
                      { nome: string; fotos: any[] }
                    >();
                    for (const foto of fotosElementos) {
                      const item = itens.find(
                        (i: any) => i.elementoId === foto.elementoId,
                      );
                      const nome =
                        item?.elemento?.nome ??
                        item?.elementoNome ??
                        foto.elementoId?.slice(0, 8);
                      if (!grupos.has(foto.elementoId))
                        grupos.set(foto.elementoId, { nome, fotos: [] });
                      grupos.get(foto.elementoId)!.fotos.push(foto);
                    }
                    return (
                      <div className="space-y-5">
                        {Array.from(grupos.entries()).map(([eId, grupo]) => (
                          <div key={eId} className="space-y-2">
                            <p className="text-xs font-semibold text-slate-600">
                              {grupo.nome}
                            </p>
                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                              {grupo.fotos.map((foto: any) => (
                                <FotoCard
                                  key={foto.id}
                                  foto={foto}
                                  onDelete={() => deleteFoto(foto.id)}
                                  canDelete={!isReclassification || canEditReclassification}
                                />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()
                )}
              </div>
            )}

            {/* Comparação de fotos (inclui fotos de elemento — parceiro não vê) */}
            {!isParceiro && fotos.length >= 2 && (
              <section className="border-t pt-5 space-y-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Comparação de fotos
                </p>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Select value={compareAId} onValueChange={setCompareAId}>
                    <SelectTrigger className="flex-1" aria-label="Selecionar foto A para comparação">
                      <SelectValue placeholder="Foto A..." />
                    </SelectTrigger>
                    <SelectContent>
                      {fotos.map((foto: any) => (
                        <SelectItem key={foto.id} value={foto.id}>
                          {fotoLabel(foto)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={compareBId} onValueChange={setCompareBId}>
                    <SelectTrigger className="flex-1" aria-label="Selecionar foto B para comparação">
                      <SelectValue placeholder="Foto B..." />
                    </SelectTrigger>
                    <SelectContent>
                      {fotos.map((foto: any) => (
                        <SelectItem key={foto.id} value={foto.id}>
                          {fotoLabel(foto)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!compareAId || !compareBId}
                    onClick={() => setCompareOpen(true)}
                  >
                    Comparar
                  </Button>
                </div>
              </section>
            )}
          </TabsContent>

          {/* ── Tab: Reparos (somente RT/Admin) ── */}
          {!isParceiro && (
            <TabsContent value="reparos" className="space-y-4 mt-4">
              <Card className="bg-slate-50 border-slate-200">
                <CardContent className="pt-4">
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      className="col-span-2"
                      placeholder="Descrição do reparo"
                      value={reparoForm.descricao}
                      onChange={(e) =>
                        setReparoForm((f) => ({
                          ...f,
                          descricao: e.target.value,
                        }))
                      }
                    />
                    <Input
                      placeholder="Número da NF"
                      value={reparoForm.numeroNf}
                      onChange={(e) =>
                        setReparoForm((f) => ({
                          ...f,
                          numeroNf: e.target.value,
                        }))
                      }
                    />
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Valor em R$"
                      value={reparoForm.valor}
                      onChange={(e) =>
                        setReparoForm((f) => ({ ...f, valor: e.target.value }))
                      }
                    />
                    <Input
                      placeholder="Nome do fornecedor"
                      value={reparoForm.fornecedor}
                      onChange={(e) =>
                        setReparoForm((f) => ({
                          ...f,
                          fornecedor: e.target.value,
                        }))
                      }
                    />
                    <Button
                      size="sm"
                      className="col-span-2"
                      disabled={
                        createReparoMutation.isPending ||
                        (isReclassification && !canEditReclassification)
                      }
                      onClick={() => createReparoMutation.mutate()}
                    >
                      {createReparoMutation.isPending && (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      )}
                      Adicionar Reparo
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {reparosLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : reparos.length === 0 ? (
                <p className="text-sm italic text-slate-500">
                  Nenhum reparo registrado ainda.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-xs text-slate-500">
                        <th className="pb-2 pr-4">Descrição</th>
                        <th className="pb-2 pr-4">Nº NF</th>
                        <th className="pb-2 pr-4">Valor</th>
                        <th className="pb-2 pr-4">Fornecedor</th>
                        <th className="pb-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {reparos.map((reparo: any) => (
                        <ReparoRow
                          key={reparo.id}
                          reparo={reparo}
                          onDelete={() => deleteReparo(reparo.id)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>
          )}

          {/* ── Tab: Conclusão ── */}
          <TabsContent value="conclusao" className="mt-4">
            {laudo.conclusao ? (
              <Card>
                <CardContent className="pt-4 space-y-3">
                  {(laudo as any).classificacaoMonta && (
                    <div>
                      <p className="text-xs text-slate-500 mb-1 font-medium">
                        Classificação de Monta:
                      </p>
                      <Badge
                        className={
                          (laudo as any).classificacaoMonta === "baixa_monta"
                            ? "bg-green-100 text-green-700"
                            : (laudo as any).classificacaoMonta ===
                                "media_monta"
                              ? "bg-amber-100 text-amber-700"
                              : (laudo as any).classificacaoMonta ===
                                  "alta_monta"
                                ? "bg-orange-100 text-orange-700"
                                : "bg-red-100 text-red-700"
                        }
                      >
                        {(laudo as any).classificacaoMonta === "baixa_monta"
                          ? "Baixa Monta"
                          : (laudo as any).classificacaoMonta === "media_monta"
                            ? "Média Monta"
                            : (laudo as any).classificacaoMonta === "alta_monta"
                              ? "Alta Monta"
                              : "Remontagem Total"}
                      </Badge>
                    </div>
                  )}
                  <Badge className={CONCLUSAO_COLORS[laudo.conclusao] ?? ""}>
                    {CONCLUSAO_LABELS[laudo.conclusao] ?? laudo.conclusao}
                  </Badge>
                  <div>
                    <p className="text-xs text-slate-500 mb-1 font-medium">
                      Justificativa:
                    </p>
                    <p className="text-sm">{laudo.justificativa}</p>
                  </div>
                </CardContent>
              </Card>
            ) : isParceiro ? (
              <div className="text-center py-12 text-slate-500">
                <p className="text-sm">
                  {laudo.status === "rascunho" || laudo.status === "em_vistoria"
                    ? "O laudo está aguardando avaliação do Responsável Técnico."
                    : "A conclusão técnica está sendo elaborada pelo Responsável Técnico."}
                </p>
              </div>
            ) : laudo.status === "em_avaliacao_rt" && isRt ? (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Conclusão Técnica</CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                  {/* Classificação de Monta */}
                  <div>
                    <Label className="text-sm font-medium mb-3 block">
                      Classificação de Monta
                    </Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {(
                        [
                          {
                            valor: "baixa_monta",
                            label: "Baixa Monta",
                            cor: "text-green-600 border-green-300 bg-green-50",
                          },
                          {
                            valor: "media_monta",
                            label: "Média Monta",
                            cor: "text-amber-600 border-amber-300 bg-amber-50",
                          },
                          {
                            valor: "alta_monta",
                            label: "Alta Monta",
                            cor: "text-orange-600 border-orange-300 bg-orange-50",
                          },
                          {
                            valor: "remontagem_total",
                            label: "Remontagem Total",
                            cor: "text-red-600 border-red-300 bg-red-50",
                          },
                        ] as const
                      ).map((op) => (
                        <button
                          key={op.valor}
                          type="button"
                          onClick={() =>
                            setConclusaoForm((f) => ({
                              ...f,
                              classificacaoMonta: op.valor,
                            }))
                          }
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                            conclusaoForm.classificacaoMonta === op.valor
                              ? op.cor + " ring-2 ring-offset-1 ring-current"
                              : "text-slate-600 border-slate-200 hover:bg-slate-50"
                          }`}
                        >
                          <span
                            className={`w-3 h-3 rounded-full shrink-0 ${
                              op.valor === "baixa_monta"
                                ? "bg-green-500"
                                : op.valor === "media_monta"
                                  ? "bg-amber-500"
                                  : op.valor === "alta_monta"
                                    ? "bg-orange-500"
                                    : "bg-red-500"
                            }`}
                          />
                          {op.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Recuperável / Irrecuperável */}
                  <div>
                    <Label className="text-sm font-medium mb-2 block">
                      Situação Estrutural
                    </Label>
                    <RadioGroup
                      value={conclusaoForm.conclusao}
                      onValueChange={(v) =>
                        setConclusaoForm((f) => ({ ...f, conclusao: v as any }))
                      }
                      aria-required="true"
                      className="flex gap-6"
                    >
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="recuperavel" id="recuperavel" />
                        <Label htmlFor="recuperavel">Recuperável</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem
                          value="irrecuperavel"
                          id="irrecuperavel"
                        />
                        <Label htmlFor="irrecuperavel">Irrecuperável</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Fundamentação técnica */}
                  <div>
                    <Label className="text-sm font-medium mb-2 block">
                      Fundamentação Técnica
                    </Label>
                    <Textarea
                      className="min-h-[120px]"
                      placeholder="Descreva os critérios técnicos que fundamentam a conclusão..."
                      value={conclusaoForm.justificativa}
                      onChange={(e) =>
                        setConclusaoForm((f) => ({
                          ...f,
                          justificativa: e.target.value,
                        }))
                      }
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      Mínimo de 20 caracteres (
                      {conclusaoForm.justificativa.length}/20)
                    </p>
                  </div>

                  <Button
                    className="w-full gap-2"
                    disabled={
                      !conclusaoForm.classificacaoMonta ||
                      !conclusaoForm.conclusao ||
                      conclusaoForm.justificativa.length < 20 ||
                      (isReclassification && !canEditReclassification)
                    }
                    onClick={() => setShowFinalizeDialog(true)}
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    Finalizar Laudo
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <p className="text-sm">
                  A conclusão técnica será preenchida pelo Responsável Técnico
                  após a vistoria ser finalizada.
                </p>
              </div>
            )}
          </TabsContent>

          {/* ── Tab: Nexo Causal (somente Reclassificação) ── */}
          {isReclassification && !laudo.ordemServicoId && (
            <TabsContent value="nexo-causal" className="mt-4">
              {canEditReclassification ? (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Nexo Causal</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    <div>
                      <Label className="text-sm font-medium mb-2 block">
                        Classificação do Nexo Causal
                      </Label>
                      <Select
                        value={nexoForm.nexoClassificacao}
                        onValueChange={(v) =>
                          setNexoForm((f) => ({ ...f, nexoClassificacao: v }))
                        }
                      >
                        <SelectTrigger aria-label="Classificação do nexo causal">
                          <SelectValue placeholder="Selecione a classificação" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(NEXO_CLASSIFICACAO_LABELS).map(
                            ([valor, label]) => (
                              <SelectItem key={valor} value={valor}>
                                {label}
                              </SelectItem>
                            ),
                          )}
                        </SelectContent>
                      </Select>
                      {nexoForm.nexoClassificacao && (
                        <Badge
                          className={`mt-2 ${NEXO_CLASSIFICACAO_COLORS[nexoForm.nexoClassificacao] ?? ""}`}
                        >
                          {NEXO_CLASSIFICACAO_LABELS[nexoForm.nexoClassificacao] ??
                            nexoForm.nexoClassificacao}
                        </Badge>
                      )}
                    </div>

                    <div>
                      <Label className="text-sm font-medium mb-2 block">
                        Narrativa
                      </Label>
                      <Textarea
                        className="min-h-[100px]"
                        placeholder="Descreva a narrativa do evento..."
                        value={nexoForm.nexoNarrativa}
                        onChange={(e) =>
                          setNexoForm((f) => ({
                            ...f,
                            nexoNarrativa: e.target.value,
                          }))
                        }
                      />
                    </div>

                    <div>
                      <Label className="text-sm font-medium mb-2 block">
                        Dinâmica
                      </Label>
                      <Textarea
                        className="min-h-[100px]"
                        placeholder="Descreva a dinâmica do acidente..."
                        value={nexoForm.nexoDinamica}
                        onChange={(e) =>
                          setNexoForm((f) => ({
                            ...f,
                            nexoDinamica: e.target.value,
                          }))
                        }
                      />
                    </div>

                    <div>
                      <Label className="text-sm font-medium mb-2 block">
                        Geometria
                      </Label>
                      <Textarea
                        className="min-h-[100px]"
                        placeholder="Descreva a geometria das avarias..."
                        value={nexoForm.nexoGeometria}
                        onChange={(e) =>
                          setNexoForm((f) => ({
                            ...f,
                            nexoGeometria: e.target.value,
                          }))
                        }
                      />
                    </div>

                    <div>
                      <Label className="text-sm font-medium mb-2 block">
                        Indicativos
                      </Label>
                      <Textarea
                        className="min-h-[100px]"
                        placeholder="Descreva os indicativos técnicos..."
                        value={nexoForm.nexoIndicativos}
                        onChange={(e) =>
                          setNexoForm((f) => ({
                            ...f,
                            nexoIndicativos: e.target.value,
                          }))
                        }
                      />
                    </div>

                    <Button
                      className="w-full gap-2"
                      disabled={nexoCausalMutation.isPending}
                      onClick={() => nexoCausalMutation.mutate()}
                    >
                      {nexoCausalMutation.isPending && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      Salvar Nexo Causal
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="pt-4 space-y-3">
                    {laudo.nexoClassificacao && (
                      <Badge
                        className={
                          NEXO_CLASSIFICACAO_COLORS[laudo.nexoClassificacao] ?? ""
                        }
                      >
                        {NEXO_CLASSIFICACAO_LABELS[laudo.nexoClassificacao] ??
                          laudo.nexoClassificacao}
                      </Badge>
                    )}
                    <div>
                      <p className="text-xs text-slate-500 mb-1 font-medium">
                        Narrativa:
                      </p>
                      <p className="text-sm">{laudo.nexoNarrativa || "—"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1 font-medium">
                        Dinâmica:
                      </p>
                      <p className="text-sm">{laudo.nexoDinamica || "—"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1 font-medium">
                        Geometria:
                      </p>
                      <p className="text-sm">{laudo.nexoGeometria || "—"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1 font-medium">
                        Indicativos:
                      </p>
                      <p className="text-sm">{laudo.nexoIndicativos || "—"}</p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          )}
        </Tabs>

        {/* Finalize dialog */}
        <A1SigningDialog open={signingOpen} onOpenChange={setSigningOpen} onSubmit={handleGerarPdf} rtName={profile?.nome ?? "RT atribuído"} hasCpf={!!profile?.cpf?.trim()} />
        <AlertDialog
          open={showFinalizeDialog}
          onOpenChange={setShowFinalizeDialog}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Finalizar Laudo?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta ação é irreversível. O laudo será finalizado e o PDF ficará
                disponível para download.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel autoFocus>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => finalizeMutation.mutate()}
                disabled={
                  finalizeMutation.isPending || (isReclassification && !canEditReclassification)
                }
              >
                {finalizeMutation.isPending && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                Finalizar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Dialog
          open={!!previewImage}
          onOpenChange={(open) => !open && setPreviewImage(null)}
        >
          <DialogContent className="max-w-3xl p-2">
            {previewImage && (
              <img
                src={previewImage}
                alt="Foto ampliada"
                className="w-full h-auto max-h-[85vh] object-contain rounded-md"
              />
            )}
          </DialogContent>
        </Dialog>

        {!isParceiro && (
          <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
            <DialogContent className="max-w-4xl p-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {[compareAId, compareBId].map((id) => {
                  const foto = fotos.find((f: any) => f.id === id);
                  if (!foto) return null;
                  return (
                    <div key={id} className="space-y-1">
                      <p className="text-xs text-center text-slate-500">
                        {fotoLabel(foto)}
                      </p>
                      <img
                        src={foto.storageUrl}
                        alt={fotoLabel(foto)}
                        className="w-full h-auto max-h-[70vh] object-contain rounded-md border"
                      />
                    </div>
                  );
                })}
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Shell>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function FotoCard({ foto, onDelete, canDelete }: { foto: any; onDelete: () => void; canDelete: boolean }) {
  const [showDelete, setShowDelete] = useState(false);
  return (
    <>
      <div className="relative rounded-md overflow-hidden border border-slate-200 aspect-square group">
        <img
          src={foto.storageUrl}
          alt="Foto do laudo"
          className="object-cover w-full h-full"
        />
        {canDelete && (
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <Button
              variant="destructive"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => setShowDelete(true)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
      {canDelete && (
        <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remover foto?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta ação removerá permanentemente a foto do laudo.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel autoFocus>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={onDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Remover
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}

function ReparoRow({
  reparo,
  onDelete,
}: {
  reparo: any;
  onDelete: () => void;
}) {
  const [showDelete, setShowDelete] = useState(false);
  const valorFormatado = reparo.valor
    ? Number(reparo.valor).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      })
    : "—";
  return (
    <>
      <tr className="border-b last:border-0">
        <td className="py-2 pr-4">{reparo.descricao}</td>
        <td className="py-2 pr-4">{reparo.numeroNf}</td>
        <td className="py-2 pr-4">{valorFormatado}</td>
        <td className="py-2 pr-4">{reparo.fornecedor}</td>
        <td className="py-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-destructive"
            onClick={() => setShowDelete(true)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </td>
      </tr>
      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover reparo?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel autoFocus>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
